const KNOWN_SPORT_SLUGS = new Set(["sumo", "judo", "bjj"]);

const currentSportSlugFromPage = () => {
  const fromBody = document.body?.dataset?.sportSlug?.trim().toLowerCase();
  if (fromBody && KNOWN_SPORT_SLUGS.has(fromBody)) return fromBody;
  const first = window.location.pathname.split("/").filter(Boolean)[0];
  if (first && KNOWN_SPORT_SLUGS.has(first)) return first;
  return "sumo";
};

const qrSessionJsonUrl = (id) => `/${currentSportSlugFromPage()}/qr-sessions/${id}`;

const sportLeaderboardUrl = () => `/${currentSportSlugFromPage()}/leaderboard`;
const sportLeaderboardWithBoutRecordedUrl = () =>
  `${sportLeaderboardUrl()}?bout_recorded=1`;

const escapeHtml = (s) => {
  const d = document.createElement("div");
  d.textContent = s;
  return d.innerHTML;
};

const initReportSessionFormSync = () => {
  const form = document.querySelector('form[action*="dashboard/report_qr_session"]');
  if (!form || form.dataset.reportSessionSync === "1") return;
  form.dataset.reportSessionSync = "1";

  const sessionInput = form.querySelector('input[name="qr_report[session_id]"]');
  const winnerInput = form.querySelector("#qr_report_winner_id");
  const loserInput = form.querySelector("#qr_report_loser_id");
  const occurredInput = form.querySelector("#qr_report_occurred_at");
  const btnWon = document.getElementById("qr-report-i-won");
  const btnLost = document.getElementById("qr-report-i-lost");
  const waitEl = document.getElementById("report-submit-wait");
  const opponentLiveEl = document.getElementById("qr-session-opponent-live");
  const reportsLiveEl = document.getElementById("qr-session-reports-live");
  const disagreementLiveEl = document.getElementById("qr-session-disagreement-live");

  if (!sessionInput || !winnerInput || !loserInput || !occurredInput || !btnWon || !btnLost) return;

  const myWrestlerId = Number(form.dataset.myWrestlerId);
  // Filmed bouts are confirmed after the clip lands: while the video is still
  // uploading, neither athlete can state the result (enforced server-side in
  // RecordQrSessionResult; mirrored here so the buttons stay disabled live).
  const filming = form.dataset.filming === "true";
  let clipAttached = form.dataset.clipAttached === "true";
  const clipGated = () => filming && !clipAttached;
  let pollTimer = null;
  let lastKnownSessionStatus = null;
  let lastParticipants = [];

  const clearPoll = () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  };

  const clearHiddenOutcome = () => {
    winnerInput.value = "";
    loserInput.value = "";
    occurredInput.value = "";
  };

  const setOutcomeDisabled = (disabled) => {
    btnWon.disabled = disabled;
    btnLost.disabled = disabled;
  };

  const iAmInSession = () =>
    lastParticipants.length >= 2 && lastParticipants.some((p) => Number(p.id) === myWrestlerId);

  const applyRealtimeUi = (data) => {
    const rt = data.realtime || {};

    if (data.status === "open") {
      if (opponentLiveEl) {
        opponentLiveEl.textContent = "Waiting for opponent to scan and join.";
      }
      if (reportsLiveEl) reportsLiveEl.innerHTML = "";
      if (disagreementLiveEl) {
        disagreementLiveEl.style.display = "none";
        disagreementLiveEl.textContent = "";
      }
      return;
    }

    if (data.status === "joined") {
      if (opponentLiveEl) {
        if (rt.opponentJoined) {
          const suffix = rt.opponentAlias ? ` (${rt.opponentAlias})` : "";
          opponentLiveEl.textContent = `Opponent joined${suffix}.`;
        } else {
          opponentLiveEl.textContent = "";
        }
      }

      if (reportsLiveEl) {
        const rows = Array.isArray(rt.reportsSummary) ? rt.reportsSummary : [];
        if (rows.length === 0) {
          reportsLiveEl.innerHTML =
            "<li class=\"help\" style=\"margin:0;\">No result submitted yet — tap I won / I lost when ready.</li>";
        } else {
          reportsLiveEl.innerHTML = rows
            .map((row) => {
              const who = row.reporterAlias || "Athlete";
              const line = row.outcomeLine || "";
              return `<li class=\"help\" style=\"margin:0.15rem 0 0;\"><strong>${escapeHtml(who)}</strong> reported: ${escapeHtml(line)}</li>`;
            })
            .join("");
        }
      }

      if (disagreementLiveEl) {
        if (rt.disagreement) {
          disagreementLiveEl.style.display = "";
          disagreementLiveEl.textContent =
            "Results do not match. Both athletes must agree on the same winner and record the bout in the same minute. Please submit again.";
        } else {
          disagreementLiveEl.style.display = "none";
          disagreementLiveEl.textContent = "";
        }
      }
    }
  };

  const applySessionPayload = (data) => {
    if (!data.participants) return;
    lastParticipants = data.participants;
    if (typeof data.clipAttached === "boolean") clipAttached = data.clipAttached;

    const waiting = data.status === "open";
    if (waitEl) {
      if (waiting) {
        waitEl.textContent = "Waiting for opponent…";
        waitEl.style.display = "";
      } else if (clipGated() && data.status === "joined") {
        waitEl.textContent = "Waiting for the bout video to upload…";
        waitEl.style.display = "";
      } else {
        waitEl.style.display = "none";
      }
    }

    const completed = data.status === "completed";
    const canPick =
      !completed && !waiting && data.status === "joined" && lastParticipants.length >= 2 && iAmInSession() && !clipGated();

    setOutcomeDisabled(!canPick);
    applyRealtimeUi(data);
  };

  const submitOutcome = (iWon) => {
    if (!iAmInSession() || lastParticipants.length < 2) return;
    if (clipGated()) return;

    const other = lastParticipants.find((p) => Number(p.id) !== myWrestlerId);
    if (!other) return;

    const winnerId = iWon ? myWrestlerId : Number(other.id);
    const loserId = iWon ? Number(other.id) : myWrestlerId;
    winnerInput.value = String(winnerId);
    loserInput.value = String(loserId);
    occurredInput.value = new Date().toISOString();
    form.requestSubmit();
  };

  const fetchSession = async () => {
    const raw = sessionInput.value.trim();
    if (!raw || !/^\d+$/.test(raw)) return;

    try {
      const res = await fetch(qrSessionJsonUrl(raw), { headers: { Accept: "application/json" } });
      if (!res.ok) return;
      const data = await res.json();
      applySessionPayload(data);
      const prev = lastKnownSessionStatus;
      lastKnownSessionStatus = data.status;
      if (data.status === "completed") {
        clearPoll();
        setOutcomeDisabled(true);
        if (waitEl) waitEl.style.display = "none";
        clearHiddenOutcome();
        if (prev === "open" || prev === "joined") {
          window.location.assign(sportLeaderboardWithBoutRecordedUrl());
        }
      }
    } catch (_) {
      /* ignore transient errors while polling */
    }
  };

  const startPoll = () => {
    clearPoll();
    fetchSession();
    pollTimer = setInterval(fetchSession, 2000);
  };

  const onSessionInputChange = () => {
    const raw = sessionInput.value.trim();
    clearPoll();
    lastParticipants = [];
    lastKnownSessionStatus = null;
    clearHiddenOutcome();

    if (!raw || !/^\d+$/.test(raw)) {
      setOutcomeDisabled(true);
      if (waitEl) waitEl.style.display = "none";
      return;
    }
    startPoll();
  };

  btnWon.addEventListener("click", () => submitOutcome(true));
  btnLost.addEventListener("click", () => submitOutcome(false));

  sessionInput.addEventListener("input", onSessionInputChange);
  sessionInput.addEventListener("change", onSessionInputChange);

  if (/^\d+$/.test(sessionInput.value.trim())) {
    startPoll();
  } else {
    setOutcomeDisabled(true);
  }

  window.addEventListener("beforeunload", clearPoll);
};

const initLiveInitiatedSessionWatch = () => {
  const el = document.getElementById("dashboard-live-qr-watch");
  if (!el || el.dataset.liveQrWatchBound === "1") return;
  el.dataset.liveQrWatchBound = "1";

  const ids = (el.dataset.sessionIds || "").split(/\s+/).filter(Boolean);
  if (!ids.length) return;

  let timer = null;
  const tick = async () => {
    for (const id of ids) {
      try {
        const res = await fetch(qrSessionJsonUrl(id), { headers: { Accept: "application/json" } });
        if (!res.ok) continue;
        const data = await res.json();

        const waitingTools = document.querySelector(`[data-live-session-tools="${id}"]`);
        if (waitingTools) {
          if (data.status === "open") {
            waitingTools.style.display = "";
          } else if (data.status === "joined") {
            waitingTools.style.display = "none";
          }
        }

        if (data.status === "completed") {
          if (timer) clearInterval(timer);
          window.location.assign(sportLeaderboardWithBoutRecordedUrl());
          return;
        }
      } catch (_) {
        /* ignore */
      }
    }
  };

  timer = setInterval(tick, 2000);
  tick();
  window.addEventListener("beforeunload", () => {
    if (timer) clearInterval(timer);
  });
};

document.addEventListener("DOMContentLoaded", initReportSessionFormSync);
document.addEventListener("turbo:load", initReportSessionFormSync);
document.addEventListener("DOMContentLoaded", initLiveInitiatedSessionWatch);
document.addEventListener("turbo:load", initLiveInitiatedSessionWatch);
